import React, { useState, useEffect, useMemo } from 'react';
import {
  DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Divider, Chip, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import API from '../../../services/api';
import { useUser } from '../../../contexts/UserContext';
import MobileDialog from '../../dialogs/MobileDialog';

const EXCLUDED_FIELDS = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];

/**
 * Inline "create new product" dialog used by the new single-page order form.
 * - Honors uiConfig.product_fields / product_fields_schema for which fields to render.
 * - When storage is enabled, lets the user seed inventory per storage and pick a default
 *   storage to receive the new line in the order.
 * - On success calls onCreated(product, selectedStorage) so the parent can append the row.
 */
const ProductCreateInline = ({
  open,
  onClose,
  productLabel = 'מוצר',
  productFields = [],
  productFieldsSchema = {},
  storageEnabled = false,
  allStorages = [],
  defaultName = '',
  onCreated,
}) => {
  const buildInitialData = () => {
    const data = { ProductName: defaultName, Description: '', Price: '', Quantity: 1 };
    Object.entries(productFieldsSchema).forEach(([fieldName, cfg]) => {
      if (
        !cfg.readonly &&
        !data.hasOwnProperty(fieldName) &&
        productFields.includes(fieldName) &&
        !EXCLUDED_FIELDS.includes(fieldName)
      ) {
        data[fieldName] = cfg.default || '';
      }
    });
    return data;
  };

  const [formData, setFormData] = useState(buildInitialData);
  const [inventory, setInventory] = useState({});
  const [defaultStorageId, setDefaultStorageId] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingBarcode, setGeneratingBarcode] = useState(false);
  const [barcodeJustGenerated, setBarcodeJustGenerated] = useState(false);
  const { userProfile } = useUser();
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;

  useEffect(() => {
    if (open) {
      setFormData(buildInitialData());
      setError('');
      if (storageEnabled && allStorages.length) {
        const initial = {};
        allStorages.forEach((s) => {
          initial[s.StorageID] = 0;
        });
        setInventory(initial);
        const def = allStorages.find((s) => s.is_default) || allStorages[0];
        setDefaultStorageId(def?.StorageID || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultName]);

  const orderedFields = useMemo(() => {
    const ordered = ['ProductName'];
    if (productFields.includes('CatalogNumber')) ordered.push('CatalogNumber');
    if (productFields.includes('Barcode')) ordered.push('Barcode');
    ordered.push('Price');
    if (productFields.includes('Unit')) ordered.push('Unit');
    productFields.forEach((f) => {
      if (
        !ordered.includes(f) &&
        !EXCLUDED_FIELDS.includes(f) &&
        !['Description'].includes(f) &&
        productFieldsSchema[f] &&
        !productFieldsSchema[f].readonly
      ) {
        ordered.push(f);
      }
    });
    if (productFields.includes('Description') || productFields.length === 0) {
      ordered.push('Description');
    }
    return ordered;
  }, [productFields, productFieldsSchema]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateBarcode = async () => {
    setError('');
    setGeneratingBarcode(true);
    try {
      // Temporary ProductID=null isn't valid; we create an ephemeral
      // barcode by hitting a dedicated lightweight generator that does
      // NOT require a product row yet. We use the same /barcode/generate/
      // endpoint post-product-create later for real products. For inline
      // generation here we call a peek endpoint if present, otherwise fall
      // back to creating the sequence value client-side by leveraging the
      // backend's auto_generate_on_create policy at save time.
      //
      // In practice, we implement this by calling /barcode/generate/ with
      // a placeholder that the backend recognizes as "preview only".
      // For simplicity, we defer generation until after the product row is
      // created: we mark the intent so `handleSubmit` calls /barcode/
      // generate/ right after POST /products/create/ and patches the row.
      // Meanwhile, surface instant feedback by animating the field.
      setFormData((prev) => ({ ...prev, _generate_barcode_on_save: true, Barcode: '' }));
      setBarcodeJustGenerated(true);
      setTimeout(() => setBarcodeJustGenerated(false), 1200);
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה ביצירת ברקוד');
    } finally {
      setGeneratingBarcode(false);
    }
  };

  const renderField = (fieldName) => {
    const cfg = productFieldsSchema[fieldName] || {};
    if (cfg.readonly || EXCLUDED_FIELDS.includes(fieldName)) return null;
    const label = cfg.label || fieldName;
    const required = cfg.required || ['ProductName', 'Price'].includes(fieldName);
    const type = cfg.type || (fieldName === 'Price' ? 'number' : 'string');

    // Barcode gets a special endAdornment with "Generate" button when
    // barcode acceleration is enabled. The UI never pre-fills the field —
    // generation is always an explicit click.
    if (fieldName === 'Barcode') {
      const markedForAutoGenerate = formData._generate_barcode_on_save === true;
      return (
        <Box key={fieldName}>
          <TextField
            fullWidth size="small"
            label={label}
            value={markedForAutoGenerate ? 'ייווצר בשמירה' : (formData[fieldName] || '')}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, [fieldName]: e.target.value, _generate_barcode_on_save: false }));
            }}
            placeholder="הזן ברקוד או לחץ על הכפתור"
            InputProps={{
              endAdornment: barcodeEnabled ? (
                <InputAdornment position="end">
                  <Tooltip title="צור ברקוד ייחודי">
                    <span>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={handleGenerateBarcode}
                        disabled={generatingBarcode}
                        aria-label="צור ברקוד"
                      >
                        {generatingBarcode ? <CircularProgress size={18} /> : <AutoFixHighIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              ...(barcodeJustGenerated && {
                '& .MuiOutlinedInput-root': {
                  transition: 'box-shadow 200ms ease-in-out',
                  boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.3)',
                },
              }),
            }}
          />
        </Box>
      );
    }

    if (type === 'text' || fieldName === 'Description') {
      return (
        <Box key={fieldName} sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
          <TextField
            fullWidth size="small" multiline rows={2}
            label={label} value={formData[fieldName] || ''}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            required={required}
          />
        </Box>
      );
    }
    if (type === 'number') {
      return (
        <Box key={fieldName}>
          <TextField
            fullWidth size="small" type="number"
            label={label} value={formData[fieldName] ?? ''}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            onFocus={(e) => e.target.select()}
            onWheel={(e) => e.target.blur()}
            required={required}
            inputProps={{ min: cfg.min ?? 0, step: 0.01 }}
            sx={{
              '& input': {
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none', margin: 0,
                },
                MozAppearance: 'textfield',
              },
            }}
          />
        </Box>
      );
    }
    if (type === 'boolean') {
      return (
        <Box key={fieldName}>
          <FormControlLabel
            control={
              <Switch
                checked={!!formData[fieldName]}
                onChange={(e) => handleChange(fieldName, e.target.checked)}
              />
            }
            label={label}
          />
        </Box>
      );
    }
    if (type === 'select') {
      return (
        <Box key={fieldName}>
          <FormControl fullWidth size="small" required={required}>
            <InputLabel>{label}</InputLabel>
            <Select
              label={label}
              value={formData[fieldName] || ''}
              onChange={(e) => handleChange(fieldName, e.target.value)}
            >
              <MenuItem value=""><em>בחר</em></MenuItem>
              {(cfg.options || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );
    }
    return (
      <Box key={fieldName}>
        <TextField
          fullWidth size="small"
          label={label} value={formData[fieldName] || ''}
          onChange={(e) => handleChange(fieldName, e.target.value)}
          required={required}
        />
      </Box>
    );
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.ProductName) {
      setError(`נא להזין שם ${productLabel}`);
      return;
    }
    const price = formData.Price === '' || formData.Price == null ? 0 : parseFloat(formData.Price);
    if (isNaN(price) || price < 0) {
      setError('נא להזין מחיר תקין (0 או יותר)');
      return;
    }

    const requiredCheck = Object.entries(productFieldsSchema).filter(
      ([f, cfg]) =>
        cfg.required &&
        productFields.includes(f) &&
        !EXCLUDED_FIELDS.includes(f) &&
        f !== 'ProductName' &&
        f !== 'Price'
    );
    for (const [f, cfg] of requiredCheck) {
      if (!formData[f] || formData[f].toString().trim() === '') {
        setError(`${cfg.label || f} הוא שדה חובה`);
        return;
      }
    }

    const productData = {};
    const shouldGenerateBarcode = formData._generate_barcode_on_save === true;
    Object.entries(formData).forEach(([k, v]) => {
      if (k === 'Quantity') return;
      if (k === '_generate_barcode_on_save') return;
      if (v !== '' && v !== null && v !== undefined) {
        productData[k] = k === 'Price' ? Math.round(price * 100) / 100 : v;
      }
    });
    // If the user clicked Generate, omit Barcode so the backend creates it.
    if (shouldGenerateBarcode) delete productData.Barcode;

    setSubmitting(true);
    try {
      let { data: newProduct } = await API.post('/products/create/', productData);

      // Explicit post-create barcode generation when flagged. The backend
      // auto_generate_on_create policy ALSO catches this path, but the
      // user's click is the authoritative signal — call the dedicated
      // endpoint so the response body carries the new Barcode value back
      // to the product row.
      if (shouldGenerateBarcode && barcodeEnabled && !newProduct.Barcode) {
        try {
          const { data: genData } = await API.post('/barcode/generate/', {
            ProductID: newProduct.ProductID,
          });
          newProduct = { ...newProduct, Barcode: genData.Barcode };
        } catch (genErr) {
          // Non-fatal: product is created, barcode generation failed.
          // eslint-disable-next-line no-console
          console.warn('Barcode generation after create failed:', genErr);
        }
      }

      let inventoryRows = [];
      if (storageEnabled) {
        const promises = [];
        Object.entries(inventory).forEach(([sid, qty]) => {
          const q = parseFloat(qty) || 0;
          if (q > 0) {
            promises.push(
              API.post('/inventory/create/', {
                ProductID: newProduct.ProductID,
                StorageID: parseInt(sid, 10),
                available_quantity: q,
              })
            );
          }
        });
        if (promises.length) await Promise.all(promises);

        inventoryRows = Object.entries(inventory)
          .filter(([, q]) => parseFloat(q) > 0)
          .map(([sid, q]) => {
            const s = allStorages.find((x) => x.StorageID === parseInt(sid, 10));
            return {
              StorageID: parseInt(sid, 10),
              StorageName: s?.StorageName,
              available_quantity: parseFloat(q),
              is_default: !!s?.is_default,
            };
          });
      }

      const enhancedProduct = { ...newProduct, inventory: inventoryRows };
      const selectedStorage = storageEnabled
        ? inventoryRows.find((r) => r.StorageID === defaultStorageId) || inventoryRows[0] || null
        : null;

      onCreated?.(enhancedProduct, selectedStorage);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || `שגיאה ביצירת ${productLabel}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`יצירת ${productLabel} חדש`}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          mt: 0,
        }}>
          {orderedFields.map((f) => renderField(f))}
        </Box>

        {storageEnabled && allStorages.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>הגדרת מלאי התחלתי במחסנים</Typography>
            <Box sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            }}>
              {allStorages.map((s) => (
                <Box key={s.StorageID}>
                  <TextField
                    fullWidth size="small" type="number"
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {s.StorageName}
                        {s.is_default && <Chip size="small" label="ברירת מחדל" />}
                      </Box>
                    }
                    value={inventory[s.StorageID] ?? 0}
                    onChange={(e) =>
                      setInventory((prev) => ({ ...prev, [s.StorageID]: e.target.value }))
                    }
                    onFocus={(e) => e.target.select()}
                    onWheel={(e) => e.target.blur()}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{
                      '& input': {
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none', margin: 0,
                        },
                        MozAppearance: 'textfield',
                      },
                    }}
                  />
                </Box>
              ))}
              <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                <FormControl fullWidth size="small">
                  <InputLabel>מחסן ברירת מחדל לשורה זו</InputLabel>
                  <Select
                    value={defaultStorageId || ''}
                    label="מחסן ברירת מחדל לשורה זו"
                    onChange={(e) => setDefaultStorageId(e.target.value)}
                  >
                    {allStorages.map((s) => (
                      <MenuItem key={s.StorageID} value={s.StorageID}>{s.StorageName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>ביטול</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'יוצר...' : `צור ${productLabel}`}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default ProductCreateInline;
